import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import useMountEffect from '../../hooks/useMountEffect';
import { getWorkOrderLabels, isWorkOrderStarred } from '../../data/workOrderStarLabels';
import { Button, List, Modal, Row, Select, Tag } from 'antd';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import {
	workOrdersRestCrudThunksForSupplier,
	appendWorkOrderNotesForSupplier,
	appendContractorWorkOrderNotes,
	getSupplierMeetingToken,
	getSupplierOWLiveShortenedLink,
} from '../../thunks/work_orders_thunks';
import {
	fetchAllUsersForSuppliers,
	addBuyerSubscribersForSuppliers,
	addSupplierSubscribersForSuppliers,
	removeSupplierSubscriberForSuppliers,
	fetchAllContractorWOUsersForSuppliers,
	addContractorSubscribers,
	addSubSubscribers,
	removeSubSubscribers,
} from '../../thunks/work_orders_subscribers_thunks';
import {
	NOTE_TYPE_LOG,
	NOTE_TYPE_SERVICE_CALL_CHECK_IN,
	NOTE_TYPE_SERVICE_CALL_CHECK_OUT,
} from '../../constants/NoteTypes';
import { ContactAvatar } from '../contact_avatar/ContactAvatar';
import {
	fetchAllContractorWorkOrderNotes,
	fetchAllWorkOrderNotesForSupplier,
} from '../../thunks/messages_thunks';
import { debounce } from '../../utils/PerformanceUtils';
import AvatarGroup from '../avatar_group/AvatarGroup';
import RemoveSubscriberButton from '../work_orders_detail_notes_page/RemoveSubscriberButton';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { getWorkOrderSelector } from '../../selectors/workOrderSelectors';
import {
	getContractorOrWorkOrderId,
	getSanitisedWorkOrder,
	getOwnThreadWOIdsToListenForWorkOrder,
	getPayloadToMarkNotesRead,
} from '../../utils/WorkOrderUtils';
import { formatAndSortUserContacts } from '../../utils/DataFormatterUtils';
import { WORK_ORDERS_CRUD_ACTION_CREATORS } from '../../actions/work_orders_actions';
import { WEB_SOCKET_ENTITY_TYPES } from '../../utils/WebSocketUtils';
import CommonNotesChatPage from '../common_notes_chat_page/CommonNotesChatPage';
import CommonNoteInputForm from '../common_note_input_form/CommonNoteInputForm';

const { Option } = Select;

const style = require('./SupplierWorkOrdersDetailNotesPage.less');

const secondaryFieldStyle = { fontSize: '1.15em', color: '#606060' };
const primaryFieldStyle = { fontSize: '1.2em', color: 'black' };

const WS_CONFIG = {
	entityIdName: 'workOrderId',
	entityType: WEB_SOCKET_ENTITY_TYPES.WORK_ORDER_NOTES,
	newNoteEntityType: WEB_SOCKET_ENTITY_TYPES.WORK_ORDER,
};

const SupplierWorkOrdersDetailNotesPage = ({
	workOrder,
	currentUser,
	match,
	location,
	messages,
	searched_subscribers,
	isClientView,
	fetchMessages,
	appendWorkOrderNotes,
	getMeetingToken,
	owShortenedLink,
	fetchUsers,
	getWorkOrder,
	addBuyerSubscribers,
	addSupplierSubscribers,
	removeSupplierSubscriber,
	updateWorkOrderPartiallyInRedux,
}) => {
	const workOrderId = match.params.id;

	const [subscriberModalVisible, setSubscriberModalVisible] = useState(false);
	const [addSubscriberModalVisible, setAddSubscriberModalVisible] = useState(false);
	const [search, setSearch] = useState('');
	const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
	const [selectValue, setSelectValue] = useState<any[]>([]);
	const [addingSubscribers, setAddingSubscribers] = useState(false);

	const fetchNotes = useCallback(
		(wo?, isClientViewOverride?: boolean) => {
			const currentWO = wo || workOrder;
			const effectiveIsClientView =
				typeof isClientViewOverride === 'boolean' ? isClientViewOverride : isClientView;
			updateWorkOrderPartiallyInRedux(getPayloadToMarkNotesRead(currentWO, effectiveIsClientView));
			return fetchMessages(workOrderId, currentWO);
		},
		[fetchMessages, workOrderId, workOrder, isClientView, updateWorkOrderPartiallyInRedux]
	);

	// The "Added/Removed Label" entries live in the notes feed (persisted server-side),
	// so they only surface on a refetch. A label change updates this work order's labels
	// in Redux — from this user's own edit (optimistic store patch) or another user's
	// edit (label WS event applied by the detail page) — so refetch the notes whenever
	// the label set changes. This is the only reliable trigger for the editor's own page:
	// the WS server does not echo a publish back to its sender, so the new_note refetch
	// path never fires for the user who made the change.
	const labelIdsKey = getWorkOrderLabels(workOrder)
		.map((label) => label.id)
		.sort()
		.join(',');
	const prevLabelIdsKeyRef = useRef(labelIdsKey);
	useEffect(() => {
		if (prevLabelIdsKeyRef.current === labelIdsKey) return;
		prevLabelIdsKeyRef.current = labelIdsKey;
		fetchNotes();
	}, [labelIdsKey, fetchNotes]);

	const starred = isWorkOrderStarred(workOrder);
	const prevStarredRef = useRef(starred);
	useEffect(() => {
		if (prevStarredRef.current === starred) return;
		prevStarredRef.current = starred;
		fetchNotes();
	}, [starred, fetchNotes]);

	// Load work order if not yet loaded. When landing on a contractor_notes URL,
	// the parent-supplied workOrder may lack contractorWo details — force a full
	// fetch in that case so contractor-scoped notes can be loaded correctly.
	//
	// We derive the client-view flag from the URL rather than reading the
	// `isClientView` prop here: the parent dispatches updateClientView(false)
	// synchronously in its componentDidMount, but the resulting Redux update
	// has not yet flowed through to this child's props by the time this
	// mount-only effect runs, so the closed-over `isClientView` would still be
	// the stale (true) value. The pathname is the source of truth the parent
	// itself uses to make that decision, so reading it directly avoids the race.
	useMountEffect(() => {
		const isContractorNotesUrl = location && location.pathname.endsWith('/contractor_notes');
		const effectiveIsClientView = isContractorNotesUrl ? false : isClientView;
		const needsFullWO = isContractorNotesUrl && !nullSafeGet('contractorWo.id', workOrder);
		if (!nullSafeGet('id', workOrder) || needsFullWO) {
			getWorkOrder(workOrderId).then((wo) => {
				fetchNotes(getSanitisedWorkOrder(wo, effectiveIsClientView), effectiveIsClientView);
			});
		} else {
			// WO already loaded: still fetch notes here using the URL-derived view,
			// not the stale `isClientView` prop. The child CommonNotesChatPage's
			// own mount effect would otherwise call fetchNotes() with the parent's
			// pre-`updateClientView(false)` value (true) on contractor_notes URLs
			// and mark the wrong channel's notes as read.
			fetchNotes(getSanitisedWorkOrder(workOrder, effectiveIsClientView), effectiveIsClientView);
		}
	});

	const appendNote = useCallback(
		(note: any) =>
			appendWorkOrderNotes({ id: getContractorOrWorkOrderId(workOrder), note }, workOrder),
		[appendWorkOrderNotes, workOrder]
	);

	const getMeetingTokenForRoom = useCallback(
		() =>
			getMeetingToken(
				`${nullSafeGet('buyerCompanyId', workOrder)}-${getContractorOrWorkOrderId(workOrder)}`
			),
		[getMeetingToken, workOrder]
	);

	const getOWShortenedLink = useCallback((url: string) => owShortenedLink(url), [owShortenedLink]);

	const subscribers = (workOrder.buyerSubscribers || []).concat(
		workOrder.supplierSubscribers || []
	);

	const filteredUsers = formatAndSortUserContacts(searched_subscribers.records);

	const handleSubscriberRemoved = useCallback(
		(item: any) => () => {
			const key = nullSafeGet('contractorWo.id', workOrder)
				? 'removeSubSubscriberEmails'
				: 'removeSupplierSubscriberEmails';
			const obj = {
				id: getContractorOrWorkOrderId(workOrder),
				[key]: [item.email],
			};
			removeSupplierSubscriber(obj, workOrder).then(() => {
				getWorkOrder(workOrderId);
			});
		},
		[removeSupplierSubscriber, workOrder, workOrderId, getWorkOrder]
	);

	const handleAddSubscribers = useCallback(() => {
		setAddingSubscribers(true);
		const buyerKey = nullSafeGet('contractorWo.id', workOrder)
			? 'addContractorSubscriberEmails'
			: 'addBuyerSubscriberEmails';
		const supplierKey = nullSafeGet('contractorWo.id', workOrder)
			? 'addSubSubscriberEmails'
			: 'addSupplierSubscriberEmails';

		const objBuyer = {
			id: getContractorOrWorkOrderId(workOrder),
			[buyerKey]: selectedUsers.filter((u) => u.contactType === 'buyer').map((u) => u.email),
		};
		const objSupplier = {
			id: getContractorOrWorkOrderId(workOrder),
			[supplierKey]: selectedUsers.filter((u) => u.contactType === 'supplier').map((u) => u.email),
		};

		const promises = [
			...(Array.isArray(objBuyer[buyerKey]) && objBuyer[buyerKey].length > 0
				? [addBuyerSubscribers(objBuyer, workOrder)]
				: []),
			...(Array.isArray(objSupplier[supplierKey]) && objSupplier[supplierKey].length > 0
				? [addSupplierSubscribers(objSupplier, workOrder)]
				: []),
		];
		Promise.all(promises).then(() => {
			setSelectValue([]);
			setAddingSubscribers(false);
			setAddSubscriberModalVisible(false);
			getWorkOrder(workOrderId);
		});
	}, [
		workOrder,
		selectedUsers,
		workOrderId,
		addBuyerSubscribers,
		addSupplierSubscribers,
		getWorkOrder,
	]);

	const handleOnChange = useCallback((val: any) => {
		const updatedVal = Array.isArray(val) ? val.map((v) => JSON.parse(v)) : JSON.parse(val);
		setSelectValue(updatedVal.map((v) => JSON.stringify(v)));
		setSelectedUsers(updatedVal);
	}, []);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const debouncedFetchUsers = useCallback(
		debounce(
			(searchText: string) => {
				fetchUsers(
					searchText && searchText.trim(),
					getContractorOrWorkOrderId(workOrder),
					{},
					workOrder
				);
			},
			400,
			false
		),
		[fetchUsers, workOrder]
	);

	const handleSearchDebounced = useCallback(
		(value = '') => {
			const searchText = value && value.trim();
			setSearch(searchText);
			debouncedFetchUsers(value);
		},
		[debouncedFetchUsers]
	);

	const contractorWOId = getContractorOrWorkOrderId(workOrder);

	const renderSubscribers = useCallback(
		() => (
			<>
				<div style={{ textAlign: 'right', padding: '0', height: '0px' }}>
					<Modal
						width={600}
						title={
							<div>
								<span style={{}}>Work Order {contractorWOId}</span>
								<Button
									style={{ float: 'right', marginRight: '32px', marginTop: '-4px' }}
									onClick={() => {
										setSubscriberModalVisible(false);
										setAddSubscriberModalVisible(true);
										setSearch('');
										setSelectedUsers([]);
									}}
									type={'primary'}
								>
									Add People
								</Button>
							</div>
						}
						footer={null}
						closable={true}
						onCancel={() => setSubscriberModalVisible(false)}
						visible={subscriberModalVisible}
					>
						<List
							itemLayout="horizontal"
							dataSource={subscribers}
							renderItem={(item: any) => (
								<List.Item>
									<List.Item.Meta
										avatar={
											<ContactAvatar
												hidePopover={true}
												placement="bottomRight"
												userType="supplier"
												contact={item}
											/>
										}
										// @ts-ignore
										title={`${item.nameGiven} ${item.nameFamily}`}
										// @ts-ignore
										description={item.email}
									/>
									<RemoveSubscriberButton
										subscriber={item}
										onRemove={handleSubscriberRemoved}
										workOrder={workOrder}
									/>
								</List.Item>
							)}
						/>
					</Modal>
					<Modal
						width={600}
						title={
							<div>
								<span>Add Someone to Work Order {contractorWOId}</span>
							</div>
						}
						footer={
							<div style={{ display: 'block', width: '100%' }}>
								<Button loading={addingSubscribers} onClick={handleAddSubscribers} type={'primary'}>
									Add Subscribers
								</Button>
							</div>
						}
						closable={true}
						onCancel={() => setAddSubscriberModalVisible(false)}
						visible={addSubscriberModalVisible}
					>
						<Row key={0}>
							<Select
								mode="multiple"
								style={{ width: '100%' }}
								placeholder="Begin to type a name or email to start searching"
								onSearch={handleSearchDebounced}
								allowClear={true}
								autoClearSearchValue={true}
								onFocus={() => handleSearchDebounced()}
								onChange={handleOnChange}
								autoFocus={true}
								value={selectValue}
								optionLabelProp="label"
								dropdownClassName={style.subscriberDropdown}
								filterOption={() => true}
								notFoundContent={
									search.length > 0 ? (
										search.includes('@') ? (
											search.slice(search.indexOf('@'), search.length).includes('.') ? (
												<Option key={search} value={search}>
													<div style={{ display: 'inline-block' }}>eMail Icon</div>
													<div style={{ display: 'inline-block' }}>
														Invite {search} to join OpenWrench
													</div>
												</Option>
											) : (
												<div>Sorry! We couldn't find this user. Try searching by name!</div>
											)
										) : (
											<div>Sorry! We couldn't find this user. Try searching by email!</div>
										)
									) : null
								}
							>
								{filteredUsers.length > 0
									? filteredUsers.map((user, index) => (
											<Option
												disabled={
													user.status === 'inactive' ||
													subscribers.some(
														(subscriber) =>
															nullSafeGetOrElse('email', subscriber, '').toLowerCase() ===
															nullSafeGetOrElse('email', user, '').toLowerCase()
													)
												}
												key={`${user.email}+${user.contactType}+${index}`}
												value={JSON.stringify(user)}
												title={user.nameGiven.concat(' ', user.nameFamily)}
												{...{ label: user.nameGiven.concat(' ', user.nameFamily) }}
												style={
													user.contactType === 'supplier'
														? {
																backgroundColor: 'rgb(37, 150, 190,0.05)',
																color: 'red',
														  }
														: { backgroundColor: 'rgb(124, 72, 189,0.05)' }
												}
												className={
													user.contactType === 'supplier'
														? style.subscriberOptionSupplier
														: style.subscriberOptionBuyer
												}
											>
												<ContactAvatar
													hidePopover={true}
													placement="bottomRight"
													userType="supplier"
													contact={user}
													className={'contactAvatar'}
													style={{
														marginBottom: '6px',
														backgroundColor:
															user.contactType === 'supplier' ? '#48a7bd' : '#7c48bd',
													}}
												/>
												<div style={{ display: 'inline-block', verticalAlign: 'middle' }}>
													<div style={{ display: 'inline-block', paddingLeft: '12px' }}>
														<div style={{ display: 'block' }}>
															<div
																style={
																	user.status === 'inactive'
																		? { fontSize: '1.2em', color: 'rgba(0, 0, 0, 0.25)' }
																		: search.includes('@')
																		? secondaryFieldStyle
																		: primaryFieldStyle
																}
															>
																{user.nameGiven} {user.nameFamily}
																{user.status === 'inactive' && (
																	<span className="px-2">
																		<Tag color="default">Inactive</Tag>
																	</span>
																)}
																{subscribers.some(
																	(subscriber) =>
																		nullSafeGetOrElse('email', subscriber, '').toLowerCase() ===
																		nullSafeGetOrElse('email', user, '').toLowerCase()
																) && (
																	<span className="px-2">
																		<Tag>Already in this channel</Tag>
																	</span>
																)}
															</div>
															<div
																style={
																	user.status === 'inactive'
																		? { fontSize: '1.15em', color: 'rgba(0, 0, 0, 0.25)' }
																		: search.includes('@')
																		? primaryFieldStyle
																		: secondaryFieldStyle
																}
															>
																{user.email}
															</div>
														</div>
													</div>
												</div>
											</Option>
									  ))
									: null}
							</Select>
						</Row>
					</Modal>
				</div>
				{/* @ts-ignore */}
				<AvatarGroup
					userType="supplier"
					avatars={subscribers}
					onClick={() => setSubscriberModalVisible(true)}
					onAddClick={() => {
						setAddSubscriberModalVisible(true);
						setSearch('');
						setSelectedUsers([]);
					}}
				/>
			</>
		),
		[
			contractorWOId,
			subscribers,
			subscriberModalVisible,
			addSubscriberModalVisible,
			addingSubscribers,
			search,
			selectValue,
			filteredUsers,
			handleSubscriberRemoved,
			handleAddSubscribers,
			handleSearchDebounced,
			handleOnChange,
			workOrder,
		]
	);

	const renderInputForm = useCallback(
		({ handleTyping, onSendNote, typingDetails, newNoteCount, onRefresh }) => (
			<CommonNoteInputForm
				appendNote={appendNote}
				currentUser={currentUser}
				workOrder={workOrder}
				onTyping={handleTyping}
				onSendNote={onSendNote}
				typingDetails={typingDetails}
				newNoteCount={newNoteCount}
				onRefresh={onRefresh}
				getMeetingToken={getMeetingTokenForRoom}
				getOWShortenedLink={getOWShortenedLink}
			/>
		),
		[appendNote, currentUser, workOrder, getMeetingTokenForRoom, getOWShortenedLink]
	);

	return (
		<CommonNotesChatPage
			entityId={String(contractorWOId)}
			entityIds={getOwnThreadWOIdsToListenForWorkOrder(workOrder)}
			fetchNotes={fetchNotes}
			userType={ROLE_TYPES.SUPPLIER}
			heightSelector="supplier-workorder-detail-header"
			wsConfig={WS_CONFIG}
			currentUser={currentUser}
			messages={messages}
			renderSubscribers={renderSubscribers}
			renderInputForm={renderInputForm}
		/>
	);
};

const mapStateToProps = (state, ownProps) => ({
	workOrder: getWorkOrderSelector(state, ownProps),
	messages: state.messages,
	currentUser: state.session.currentUser,
	searched_subscribers: state.searched_subscribers,
	match: ownProps.match,
	history: ownProps.history,
	isClientView: state.clientView.isClientView,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	getWorkOrder: (id) => dispatch(workOrdersRestCrudThunksForSupplier.readOne(id)),
	fetchMessages: (workOrderId, workOrder) =>
		dispatch(
			nullSafeGet('contractorWo', workOrder)
				? fetchAllContractorWorkOrderNotes(ROLE_TYPES.SUPPLIER)(
						nullSafeGet('contractorWo.id', workOrder)
				  )
				: fetchAllWorkOrderNotesForSupplier(workOrderId)
		),
	fetchUsers: (search, woId, filters, workOrder) =>
		dispatch(
			nullSafeGet('contractorWo', workOrder)
				? fetchAllContractorWOUsersForSuppliers(search, woId, filters)()
				: fetchAllUsersForSuppliers(search, woId, filters)()
		),
	appendWorkOrderNotes: ({ id, note }, workOrder) =>
		dispatch(
			nullSafeGet('contractorWo', workOrder)
				? appendContractorWorkOrderNotes(ROLE_TYPES.SUPPLIER)({ id, note })
				: appendWorkOrderNotesForSupplier({ id, note })
		),
	addBuyerSubscribers: (obj, workOrder) =>
		dispatch(
			nullSafeGet('contractorWo', workOrder)
				? addContractorSubscribers(ROLE_TYPES.SUPPLIER)(obj)(workOrder)
				: addBuyerSubscribersForSuppliers(obj)(workOrder)
		),
	addSupplierSubscribers: (obj, workOrder) =>
		dispatch(
			nullSafeGet('contractorWo', workOrder)
				? addSubSubscribers(ROLE_TYPES.SUPPLIER)(obj)(workOrder)
				: addSupplierSubscribersForSuppliers(obj)(workOrder)
		),
	removeSupplierSubscriber: (obj, workOrder) =>
		dispatch(
			nullSafeGet('contractorWo', workOrder)
				? removeSubSubscribers(ROLE_TYPES.SUPPLIER)(obj)(workOrder)
				: removeSupplierSubscriberForSuppliers(obj)(workOrder)
		),
	getMeetingToken: (roomName) => dispatch(getSupplierMeetingToken(roomName)),
	owShortenedLink: (longUrl) => dispatch(getSupplierOWLiveShortenedLink(longUrl)),
	updateWorkOrderPartiallyInRedux: (workOrder) =>
		dispatch(WORK_ORDERS_CRUD_ACTION_CREATORS.updatePartialSuccess(workOrder)),
});

export default withRouter(
	connect(mapStateToProps, mapDispatchToProps)(SupplierWorkOrdersDetailNotesPage as any)
);
